import React, { useEffect, useState } from 'react'
import TextField from '../../components/TextField/TextField'
import config from '../../config'
import Button from '../Button/Button'
import './Login.scss'
import { useI18nSelector, useI18nState } from '../../v3/context/i18n'
import { removeLocalStorage } from '../../util'
import { doRequest } from '../../middleware'
import { AuthActionTypes } from '../../actions/auth'
import { getLoginError, LoginError, LoginErrorMessage } from './LoginError'
import { LoginHelpButton } from './RootLogin'
import { datadogLogs } from '@datadog/browser-logs'

export interface ResetPasswordProps {
  token?: string
}

const ResetPassword = (props: ResetPasswordProps) => {
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<LoginError | null>(null)
  const i18n = useI18nSelector()
  const { locale } = useI18nState()

  useEffect(() => {
    const tokenErr: LoginError = {
      root: [i18n('auth.reset.token-error'), i18n('auth.reset.token-help')],
    }
    if (props.token && props.token !== '') {
      return
    }
    // Checking the values of the current and new error - otherwise the
    // unlocalized version may show.
    if (error && error.root[0] === tokenErr.root[0]) {
      return
    }
    setError(tokenErr)
    datadogLogs.logger.error('invalid password reset token', {
      type: AuthActionTypes.Reset,
    })
  }, [props.token, i18n, error])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit(event)
    }
  }

  const handleSubmit = (event: React.SyntheticEvent) => {
    if (event) {
      event.preventDefault()
    }

    doRequest({
      url: `${config.api}/credentials/reset`,
      method: 'POST',
      payload: {
        password,
        passwordConfirm,
        token: props.token,
      },
      validateStatus: (status) => {
        return status === 200
      },
      headers: {
        'Accept-Language': locale,
      },
      type: AuthActionTypes.Reset,
    })
      .then((res) => {
        if (!res) return
        setSuccess(true)
        setError(null)
        removeLocalStorage(config.sessionKey) // prevent routing issues when resetting password while an active session exists
      })
      .catch((err) => {
        setError(getLoginError(i18n, err, AuthActionTypes.Reset))
      })
  }

  const passwordForm = (): React.ReactNode => {
    return (
      <>
        <p className="message">{i18n('auth.reset.introduction')}</p>
        <LoginErrorMessage error={error} />
        <TextField label={i18n('auth.passwordLabel')} onInput={(v) => setPassword(v)} onKeyDown={handleKeyDown} type="password" />
        <TextField label={i18n('auth.passwordConfirmation')} onInput={(v) => setPasswordConfirm(v)} onKeyDown={handleKeyDown} type="password" />
        <div className="buttons">
          <div className="actions reset">
            <Button className="submit" variant="contained" color="primary" disableElevation onClick={handleSubmit}>
              {i18n('auth.cta.resetPassword')}
            </Button>
          </div>
          <div className="helpers">
            <LoginHelpButton reqType={AuthActionTypes.Reset} />
          </div>
        </div>
      </>
    )
  }

  const passwordSuccess = (): React.ReactNode => {
    return (
      <>
        <p className="message">{i18n('auth.reset.successful')}</p>
        <div className="buttons">
          <div className="actions reset-login">
            <Button
              className="submit"
              variant="contained"
              color="primary"
              id="submit"
              onClick={() =>
                // Using react-router's push() causes a state problem here, so just reload the whole page
                (window.location.href = '/login')
              }
            >
              {i18n('auth.cta.reset.login')}
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="LoginScreen">
      <div className="Login">
        <div className="form">
          <div className="logo" />
          {success ? passwordSuccess() : passwordForm()}
        </div>
        <div className="greeting" />
      </div>
    </div>
  )
}

export default ResetPassword
